import { useMemo, useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Card, CardContent } from "@/components/ui/card";
import { Alert, AlertDescription } from "@/components/ui/alert";
import {
  Percent,
  Edit,
  Save,
  X,
  RefreshCw,
  Info,
  AlertTriangle,
} from "lucide-react";
import {
  useGetTaxRates,
  useUpsertTaxRate,
  AUTHORITY_LABEL,
  type TaxRate,
  type TaxAuthority,
} from "@/api/tax-rates";

/**
 * Output tax on a freight transportation service, per province.
 *
 * ⚠️ A PROVINCE IS NOT ONE NUMBER, which is why this screen lists COMPONENTS and
 * not "rates". Québec is 5% GST **to the CRA** plus 9.975% QST **to Revenu
 * Québec** — two governments, two returns, two invoice lines. One blended number
 * would get the customer's total right and hide that it is owed to two places.
 *
 * ⚠️ BC / SK / MB are GST-only ON PURPOSE. Their provincial taxes apply to goods
 * and to a LISTED set of services, and a standalone freight service is not on
 * those lists. Their missing PST row is a decision, not an oversight.
 *
 * Toggling a component inactive stops charging it — no destructive delete. That is
 * how QST gets switched on the day EZX registers with Revenu Québec.
 */
const PROVINCES: { code: string; name: string }[] = [
  { code: "AB", name: "Alberta" },
  { code: "BC", name: "British Columbia" },
  { code: "MB", name: "Manitoba" },
  { code: "NB", name: "New Brunswick" },
  { code: "NL", name: "Newfoundland and Labrador" },
  { code: "NS", name: "Nova Scotia" },
  { code: "NT", name: "Northwest Territories" },
  { code: "NU", name: "Nunavut" },
  { code: "ON", name: "Ontario" },
  { code: "PE", name: "Prince Edward Island" },
  { code: "QC", name: "Quebec" },
  { code: "SK", name: "Saskatchewan" },
  { code: "YT", name: "Yukon" },
];

/** Prisma Decimal arrives over the wire as a string. */
const asNumber = (v: number | string) =>
  typeof v === "number" ? v : Number(v);

const pct = (fraction: number | string) =>
  `${(asNumber(fraction) * 100).toFixed(3).replace(/\.?0+$/, "")}%`;

interface EditingState {
  percent: number; // human-facing %, e.g. 13 for 0.13
  label: string;
  active: boolean;
}

export function TaxRateForm() {
  const { data: components, isLoading, isError, refetch } = useGetTaxRates();
  const [editing, setEditing] = useState<Record<string, EditingState>>({});

  const upsert = useUpsertTaxRate({
    onSuccess: () => {
      toast.success("Saved", { description: "Tax component updated" });
      void refetch();
    },
    onError: (e: Error) =>
      toast.error("Save failed", { description: e.message }),
  });

  /** province → its components. Ontario has one; Québec has two. */
  const byProvince = useMemo(() => {
    const map: Record<string, TaxRate[]> = {};
    for (const c of components ?? []) {
      const list = map[c.province] ?? [];
      list.push(c);
      map[c.province] = list;
    }
    return map;
  }, [components]);

  const keyOf = (c: TaxRate) => `${c.province}:${c.code}`;

  const beginEdit = (c: TaxRate) =>
    setEditing((prev) => ({
      ...prev,
      [keyOf(c)]: {
        percent: Math.round(asNumber(c.fraction) * 100000) / 1000,
        label: c.label ?? "",
        active: c.active,
      },
    }));

  const cancelEdit = (k: string) =>
    setEditing((prev) => {
      const next = { ...prev };
      delete next[k];
      return next;
    });

  const updateEditing = (
    k: string,
    field: keyof EditingState,
    value: number | string | boolean
  ) =>
    setEditing((prev) => ({
      ...prev,
      [k]: {
        ...(prev[k] ?? { percent: 0, label: "", active: true }),
        [field]: value,
      } as EditingState,
    }));

  const handleSave = async (c: TaxRate) => {
    const v = editing[keyOf(c)];
    if (!v) return;
    await upsert.mutateAsync({
      province: c.province,
      // Identity, not content: a province has SEVERAL components, and upserting on
      // province alone would have one silently overwrite the other.
      authority: c.authority,
      code: c.code,
      fraction: Math.round((v.percent / 100) * 100000) / 100000,
      label: v.label.trim() || undefined,
      active: v.active,
    });
    cancelEdit(keyOf(c));
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center p-8">
        <RefreshCw className="h-6 w-6 animate-spin text-gray-400" />
      </div>
    );
  }

  if (isError) {
    return (
      <Card>
        <CardContent className="p-8 text-center text-muted-foreground">
          <AlertTriangle className="mx-auto mb-2 h-10 w-10 text-destructive opacity-70" />
          <p className="font-medium text-foreground">
            Couldn&apos;t load tax components.
          </p>
          <p className="mt-1 text-sm">
            The existing configuration has not been interpreted as missing.
          </p>
          <Button
            variant="outline"
            size="sm"
            className="mt-3"
            onClick={() => void refetch()}
          >
            Try again
          </Button>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      <h3 className="flex items-center gap-2 text-lg font-semibold">
        <Percent className="h-5 w-5" />
        Output tax — components, per province
      </h3>

      <Alert>
        <Info className="h-4 w-4" />
        <AlertDescription className="space-y-1 text-sm">
          <p>
            <strong>A province is not one number.</strong> Québec is 5% GST to
            the <em>Canada Revenue Agency</em> plus 9.975% QST to{" "}
            <em>Revenu Québec</em> — two governments, two returns. Every row
            says which one it is owed to.
          </p>
          <p>
            <strong>BC, SK and MB are GST-only on purpose.</strong> Their
            provincial taxes apply to goods and to a listed set of services, and
            a standalone freight service is not on those lists.
          </p>
          <p className="text-amber-700 dark:text-amber-500">
            <strong>QST is switched off.</strong> It is a separate registration
            with Revenu Québec — federal incorporation does not grant it, and
            neither does a GST/HST number. Charging a tax EZX cannot remit is
            worse than not charging it. Turn it on the day the accountant
            confirms.
          </p>
        </AlertDescription>
      </Alert>

      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Province</TableHead>
                <TableHead>Tax</TableHead>
                <TableHead>Remitted to</TableHead>
                <TableHead>Rate</TableHead>
                <TableHead>Label</TableHead>
                <TableHead>Charged</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {PROVINCES.flatMap(({ code, name }) => {
                const rows = byProvince[code] ?? [];

                if (rows.length === 0) {
                  return [
                    <TableRow key={code}>
                      <TableCell className="font-medium">
                        {name}{" "}
                        <span className="text-muted-foreground">({code})</span>
                      </TableCell>
                      <TableCell
                        colSpan={6}
                        className="text-destructive text-sm"
                      >
                        No tax component configured — quoting this province will
                        FAIL. A missing row is a configuration gap, not a tax
                        exemption.
                      </TableCell>
                    </TableRow>,
                  ];
                }

                // What is actually CHARGED: the active components, summed. Québec
                // shows 5% today and 14.975% the day QST is switched on.
                const charged = rows
                  .filter((r) => r.active)
                  .reduce((total, r) => total + asNumber(r.fraction), 0);

                return rows.map((c, i) => {
                  const k = keyOf(c);
                  const v = editing[k];

                  return (
                    <TableRow key={k}>
                      <TableCell className="font-medium">
                        {i === 0 && (
                          <>
                            {name}{" "}
                            <span className="text-muted-foreground">
                              ({code})
                            </span>
                            {rows.length > 1 && (
                              <div className="text-muted-foreground text-xs">
                                charged: {pct(charged)}
                              </div>
                            )}
                          </>
                        )}
                      </TableCell>

                      <TableCell>
                        <Badge variant="outline">{c.code}</Badge>
                      </TableCell>

                      <TableCell className="text-sm">
                        {AUTHORITY_LABEL[c.authority as TaxAuthority]}
                      </TableCell>

                      <TableCell>
                        {v ? (
                          <Input
                            type="number"
                            step="0.001"
                            min={0}
                            max={100}
                            className="w-24"
                            value={v.percent}
                            onChange={(e) =>
                              updateEditing(
                                k,
                                "percent",
                                Number(e.target.value)
                              )
                            }
                          />
                        ) : (
                          pct(c.fraction)
                        )}
                      </TableCell>

                      <TableCell>
                        {v ? (
                          <Input
                            className="w-56"
                            value={v.label}
                            onChange={(e) =>
                              updateEditing(k, "label", e.target.value)
                            }
                          />
                        ) : (
                          <span className="text-muted-foreground text-sm">
                            {c.label ?? "—"}
                          </span>
                        )}
                      </TableCell>

                      <TableCell>
                        {v ? (
                          <Switch
                            checked={v.active}
                            onCheckedChange={(on) =>
                              updateEditing(k, "active", on)
                            }
                          />
                        ) : c.active ? (
                          <Badge>Yes</Badge>
                        ) : (
                          <Badge variant="secondary">No — not registered</Badge>
                        )}
                      </TableCell>

                      <TableCell className="text-right">
                        {v ? (
                          <div className="flex justify-end gap-2">
                            <Button
                              size="sm"
                              onClick={() => void handleSave(c)}
                              disabled={upsert.isPending}
                            >
                              <Save className="h-4 w-4" />
                            </Button>
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => cancelEdit(k)}
                            >
                              <X className="h-4 w-4" />
                            </Button>
                          </div>
                        ) : (
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => beginEdit(c)}
                          >
                            <Edit className="h-4 w-4" />
                          </Button>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                });
              })}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}
